import React from 'react';
import { Container, Row, Col, Tabs, Tab, Badge } from 'react-bootstrap';
import { FaGavel, FaShieldAlt, FaExclamationCircle } from 'react-icons/fa';
import { motion } from 'framer-motion';
import rulesData from '../data/rulesData.json';
import SEO from '../components/SEO';
// import '../styles/Rules.css'; // Removed, using global neon styles

const Rules = () => {
    const getPunishmentBg = (text) => {
        const t = text.toLowerCase();
        if (t.includes('warning')) return 'warning';
        if (t.includes('isolation')) return 'text-info';
        if (t.includes('permanent') || t.includes('ban')) return 'danger';
        return 'secondary';
    };

    const getPunishmentClass = (text) => {
        const t = text.toLowerCase();
        if (t.includes('warning')) return 'text-dark border-warning';
        if (t.includes('isolation')) return 'text-cyan border-info';
        if (t.includes('permanent') || t.includes('ban')) return 'text-white border-danger';
        return 'text-white border-light';
    };

    return (
        <div style={{ minHeight: '100vh', paddingTop: '60px' }}>
            <SEO
                title="Official Server Rules & Regulations"
                description="Official Thalai Nagaram RP (TNRP) server rules: RDM, VDM, Metagaming, Powergaming, FailRP, New Life Rule (NLR), and community roleplay guidelines."
                keywords="TNRP Rules, Thalai Nagaram RP Server Rules, GTA 5 Tamil RP Rules, FiveM Roleplay Guidelines, TNRP FailRP RDM VDM, FiveM Tamil Rules"
                canonicalUrl="https://thalainagaram.org/rules"
            />
            {/* Neon Background Accents */}
            <div style={{ position: 'fixed', top: '20%', right: '-10%', width: '50vw', height: '50vw', background: 'radial-gradient(circle, rgba(188, 19, 254, 0.1) 0%, transparent 60%)', filter: 'blur(80px)', zIndex: -1 }} />
            <div style={{ position: 'fixed', bottom: '-10%', left: '-10%', width: '40vw', height: '40vw', background: 'radial-gradient(circle, rgba(255, 215, 0, 0.1) 0%, transparent 60%)', filter: 'blur(80px)', zIndex: -1 }} />

            <Container fluid className="py-5">
                <Row className="justify-content-center">
                    <Col lg={10}>
                        {/* Title Block */}
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="text-center mb-5 pb-5 position-relative"
                        >
                            <div className="d-inline-block px-3 py-1 mb-3 border border-warning border-opacity-50 text-neon-gold tracking-widest small fw-bold" style={{ fontFamily: 'Orbitron, sans-serif' }}>
                                OFFICIAL PROTOCOL
                            </div>
                            <h1 className="display-4 fw-bold mb-2 text-white" style={{ textShadow: '0 0 20px rgba(255, 215, 0, 0.3)', fontSize: '2rem' }}>SERVER REGULATIONS</h1>
                            <p className="lead text-secondary mx-auto mb-4" style={{ maxWidth: '600px' }}>
                                A guide to maintaining order and quality in Thalainagaram.
                            </p>
                            
                            {/* Punishment Philosophy Note */}
                            {rulesData.server.punishment_philosophy && (
                                <motion.div 
                                    initial={{ opacity: 0, scale: 0.95 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    transition={{ delay: 0.2 }}
                                    className="mx-auto mt-4 px-4 py-3 border border-danger bg-dark bg-opacity-50 rounded"
                                    style={{ maxWidth: '700px', boxShadow: '0 4px 15px rgba(220, 53, 69, 0.15)' }}
                                >
                                    <h5 className="text-danger fw-bold mb-1 d-flex align-items-center justify-content-center">
                                        <FaGavel className="me-2" /> Punishment Philosophy
                                    </h5>
                                    <p className="text-white-50 mb-0 font-monospace small">
                                        {rulesData.server.punishment_philosophy}
                                    </p>
                                </motion.div>
                            )}

                            {/* Decorative Line */}
                            <div style={{ width: '100px', height: '2px', background: 'var(--neon-gold)', margin: '2rem auto 0', boxShadow: '0 0 10px var(--neon-gold)' }} />
                        </motion.div>

                        <style>
                            {`
                                .custom-tabs .nav-link { color: rgba(255,255,255,0.5); font-family: 'Orbitron', sans-serif; border: none; padding: 1rem 2rem; font-size: 1.1rem; }
                                .custom-tabs .nav-link.active { background: transparent; color: var(--neon-gold); border-bottom: 2px solid var(--neon-gold); }
                                .custom-tabs { border-bottom: 1px solid rgba(255,255,255,0.1); }
                            `}
                        </style>
                        <Tabs
                            defaultActiveKey={rulesData.rule_sets[0]?.set_id}
                            id="rules-tabs"
                            className="mb-5 custom-tabs justify-content-center"
                        >
                            {rulesData.rule_sets.map((ruleSet) => (
                                <Tab eventKey={ruleSet.set_id} title={ruleSet.set_title} key={ruleSet.set_id}>
                                    <div className="mb-4 text-center">
                                        <p className="text-secondary fs-5">{ruleSet.description}</p>
                                    </div>
                                    <div className="d-flex flex-column gap-4">
                                        {ruleSet.rules.map((rule, idx) => (
                                            <motion.div
                                                key={rule.id}
                                                initial={{ opacity: 0, y: 20 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                transition={{ delay: idx * 0.1 }}
                                                className="p-4 position-relative overflow-hidden"
                                                style={{
                                                    background: 'var(--bg-card)',
                                                    border: '1px solid rgba(255,255,255,0.05)',
                                                    borderLeft: '4px solid var(--neon-gold)',
                                                    boxShadow: '0 4px 20px rgba(0,0,0,0.3)'
                                                }}
                                            >
                                                <div className="d-flex align-items-center mb-3">
                                                    <Badge bg="warning" text="dark" className="me-3 fs-6 px-3 py-2 rounded-0" style={{ fontFamily: 'Orbitron, sans-serif' }}>
                                                        {rule.id}
                                                    </Badge>
                                                    <h3 className="text-white h4 fw-bold mb-0" style={{ fontFamily: 'Orbitron, sans-serif' }}>
                                                        {rule.rule_name}
                                                    </h3>
                                                </div>
                                                <p className="text-warning fs-5 fw-bold mb-3">{rule.short_description}</p>
                                                <p className="text-light fs-5 lh-base mb-4 opacity-100">{rule.explanation}</p>
                                                
                                                {rule.scenario && (
                                                    <Row className="g-4 mb-4">
                                                        <Col md={6}>
                                                            <div className="p-4 border border-danger border-opacity-50 rounded bg-danger bg-opacity-10 h-100 shadow-sm">
                                                                <h6 className="text-danger fs-5 fw-bold mb-3"><FaExclamationCircle className="me-2" />{rule.scenario.violation.label}</h6>
                                                                <p className="text-white mb-0 fs-6 lh-base">{rule.scenario.violation.example}</p>
                                                            </div>
                                                        </Col>
                                                        <Col md={6}>
                                                            <div className="p-4 border border-success border-opacity-50 rounded bg-success bg-opacity-10 h-100 shadow-sm">
                                                                <h6 className="text-success fs-5 fw-bold mb-3"><FaShieldAlt className="me-2" />{rule.scenario.correct.label}</h6>
                                                                <p className="text-white mb-0 fs-6 lh-base">{rule.scenario.correct.example}</p>
                                                            </div>
                                                        </Col>
                                                    </Row>
                                                )}

                                                <div className="mt-4 pt-4 border-top border-secondary border-opacity-50">
                                                    {rule.punishment.progression ? (
                                                        <>
                                                            <span className="text-white fw-bold fs-5 me-3">Punishment Progression:</span>
                                                            {rule.punishment.progression.map((punish, pIdx) => (
                                                                <Badge key={pIdx} bg={getPunishmentBg(punish)} className={`me-2 border fw-bold py-2 px-3 fs-6 shadow-sm ${getPunishmentClass(punish)}`}>
                                                                    {punish}
                                                                </Badge>
                                                            ))}
                                                        </>
                                                    ) : (
                                                        <>
                                                            <span className="text-danger fw-bold fs-5 me-3">{rule.punishment.type}:</span>
                                                            <Badge bg="danger" className="me-2 border border-light text-white fw-bold py-2 px-3 fs-6 shadow-sm">
                                                                {rule.punishment.outcome}
                                                            </Badge>
                                                            {rule.punishment.note && (
                                                                <p className="text-danger mt-3 mb-0 fw-bold fs-6">{rule.punishment.note}</p>
                                                            )}
                                                        </>
                                                    )}
                                                </div>
                                            </motion.div>
                                        ))}
                                    </div>
                                </Tab>
                            ))}
                        </Tabs>

                    </Col>
                </Row>
            </Container>
        </div>
    );
};

export default Rules;
